import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Plus, ClipboardCheck, Calendar as CalendarIcon,
  Zap, Droplets, Flame, Home, Building2,
  DoorOpen, Trees, Wrench, Eye, Trash2,
  CheckCircle2, XCircle, AlertTriangle, Euro,
  MapPin, FileText, Thermometer, Layers,
  PaintBucket, ChefHat, Bath, Shield,
  Printer, RefreshCw, Download, Camera,
  Calculator, Kanban, ExternalLink, Search as SearchIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { downloadBlob } from '../utils/download';
import { listVisits, createVisit, updateVisit, deleteVisit, storePhotoLocal, listProperties, searchBordereau, getVisitTemplate } from '../offline/repo';
import { NewItemBadge } from '../components/NewItemBadge';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const categoryIcons = {
  'Electricite': Zap,
  'Plomberie': Droplets,
  'Chauffage / Climatisation': Flame,
  'Toiture / Charpente': Home,
  'Gros oeuvre / Structure': Building2,
  'Platrerie / Plaque de platre': Layers,
  'Isolation / Performances energetiques': Thermometer,
  'Isolation / Energetique': Thermometer,
  'Menuiseries interieures': DoorOpen,
  'Menuiseries exterieures': DoorOpen,
  'Revetements interieurs': Layers,
  'Peinture': PaintBucket,
  'Cuisine': ChefHat,
  'Salle de bain / WC': Bath,
  'Exterieur / Facade': Trees,
  'Conformite / Diagnostics': Shield,
  'Frais de chantier': Wrench,
};

const categoryColors = {
  'Electricite': 'text-yellow-500',
  'Plomberie': 'text-blue-400',
  'Chauffage / Climatisation': 'text-orange-500',
  'Toiture / Charpente': 'text-red-400',
  'Gros oeuvre / Structure': 'text-zinc-300',
  'Isolation / Performances energetiques': 'text-cyan-400',
  'Menuiseries interieures': 'text-amber-400',
  'Menuiseries exterieures': 'text-amber-500',
  'Revetements interieurs': 'text-purple-400',
  'Cuisine': 'text-pink-400',
  'Salle de bain / WC': 'text-sky-400',
  'Exterieur / Facade': 'text-emerald-400',
  'Conformite / Diagnostics': 'text-indigo-400',
};

const STATUS_OPTIONS = {
  conforme: { label: 'Conforme', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30', icon: CheckCircle2 },
  a_remplacer: { label: 'A remplacer', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', icon: XCircle },
  non_observable: { label: 'Non observable', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30', icon: AlertTriangle },
  sans_objet: { label: 'Sans objet', color: 'text-zinc-500', bg: 'bg-zinc-700/30 border-zinc-600', icon: null },
  non_verifie: { label: 'Non verifie', color: 'text-zinc-600', bg: 'bg-zinc-800 border-zinc-700', icon: null },
};

const getItemStatus = (item) => {
  // Support both old format (checked) and new format (status)
  if (item.status) return item.status;
  if (item.checked === true) return 'conforme';
  if (item.checked === false) return 'non_verifie';
  return 'non_verifie';
};

const isItemOk = (item) => {
  const s = getItemStatus(item);
  return s === 'conforme' || s === 'sans_objet';
};

// Map old a_remettre to a_remplacer for backward compat
const normalizeStatus = (s) => s === 'a_remettre' ? 'a_remplacer' : s;

// ===== Visit Detail View (Read-only report) =====
const VisitReport = ({ visit, property, onClose }) => {
  const totalItems = visit.categories?.reduce((acc, cat) => acc + cat.items.length, 0) || 0;
  const checkedItems = visit.categories?.reduce((acc, cat) => acc + cat.items.filter(i => isItemOk(i)).length, 0) || 0;
  const totalCout = visit.categories?.reduce((acc, cat) => {
    const items = cat.items.reduce((s, item) => s + (parseFloat(item.cout_estime) || 0), 0);
    const hb = (cat.hors_bordereau || []).reduce((s, h) => s + (parseFloat(h.total) || 0), 0);
    return acc + items + hb;
  }, 0) || 0;
  const pctChecked = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;

  return (
    <div className="space-y-6 max-h-[80vh] overflow-y-auto pr-2" data-testid="visit-report">
      {/* Header */}
      <div className="bg-zinc-800/50 rounded-lg p-5 border border-zinc-700">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white font-['Outfit']">Compte-rendu de visite</h2>
            <p className="text-zinc-400 text-sm mt-1">
              {visit.visit_date ? format(new Date(visit.visit_date), "EEEE dd MMMM yyyy", { locale: fr }) : 'Date non definie'}
            </p>
          </div>
          <div className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${visit.is_viable === true
              ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
              : visit.is_viable === false
                ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                : 'bg-zinc-700 text-zinc-400 border border-zinc-600'
            }`}>
            {visit.is_viable === true ? 'VIABLE' : visit.is_viable === false ? 'NON VIABLE' : 'EN EVALUATION'}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Bien</p>
            <p className="text-white font-medium">{property?.title || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Adresse</p>
            <p className="text-white">{property?.address}, {property?.postal_code} {property?.city}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Prix de vente</p>
            <p className="text-emerald-400 font-mono font-semibold">
              {property?.price ? property.price.toLocaleString('fr-FR') + ' EUR' : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Surface</p>
            <p className="text-white">{property?.surface || 'N/A'} m2</p>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold font-mono text-white">{checkedItems}/{totalItems}</p>
            <p className="text-xs text-zinc-500 mt-1">Points verifies</p>
            <Progress value={pctChecked} className="mt-2 h-1.5" />
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold font-mono text-amber-400">
              {totalCout.toLocaleString('fr-FR')} EUR
            </p>
            <p className="text-xs text-zinc-500 mt-1">Estimation travaux (postes)</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold font-mono text-blue-400">
              {(parseFloat(visit.budget_travaux_estime) || 0).toLocaleString('fr-FR')} EUR
            </p>
            <p className="text-xs text-zinc-500 mt-1">Budget global estime</p>
          </CardContent>
        </Card>
      </div>

      {/* Categories Detail */}
      {visit.categories?.map((cat, catIdx) => {
        const Icon = categoryIcons[cat.name] || Wrench;
        const color = categoryColors[cat.name] || 'text-zinc-400';
        const catChecked = cat.items.filter(i => i.checked).length;
        const catCoutItems = cat.items.reduce((s, i) => s + (parseFloat(i.cout_estime) || 0), 0);
        const catCoutHb = (cat.hors_bordereau || []).reduce((s, h) => s + (parseFloat(h.total) || 0), 0);
        const catCout = catCoutItems + catCoutHb;
        const itemsWithIssues = cat.items.filter(i => !i.checked || (parseFloat(i.cout_estime) || 0) > 0);

        return (
          <Card key={catIdx} className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className={`text-base flex items-center gap-2 ${color}`}>
                  <Icon className="w-5 h-5" />
                  {cat.name}
                </CardTitle>
                <div className="flex items-center gap-3">
                  {catCout > 0 && (
                    <span className="text-xs font-mono text-amber-400 bg-amber-500/10 px-2 py-1 rounded">
                      {catCout.toLocaleString('fr-FR')} EUR
                    </span>
                  )}
                  <span className="text-xs text-zinc-500">{catChecked}/{cat.items.length} OK</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {cat.items.map((item, itemIdx) => {
                  const itemSt = getItemStatus(item);
                  const stConfig = STATUS_OPTIONS[itemSt] || STATUS_OPTIONS.non_verifie;
                  const StIcon = stConfig.icon || AlertTriangle;
                  return (
                  <div key={itemIdx} className={`flex items-start gap-3 py-1.5 px-2 rounded text-sm ${itemSt === 'a_remettre' ? 'bg-red-500/5' : ''}`}>
                    <StIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${stConfig.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={stConfig.color}>{item.name}</span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${stConfig.bg} ${stConfig.color}`}>{stConfig.label}</Badge>
                      </div>
                      {item.notes && <p className="text-xs text-zinc-500 mt-0.5">{item.notes}</p>}
                      {(item.photo_ids || []).length > 0 && (
                        <div className="mt-1 flex gap-1 flex-wrap">
                          {item.photo_ids.map(pid => (
                            <a key={pid} href={`${API}/files/${pid}`} target="_blank" rel="noreferrer">
                              <img src={`${API}/files/${pid}`} alt="" className="h-10 w-10 object-cover rounded border border-zinc-700 hover:opacity-80" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    {(parseFloat(item.cout_estime) || 0) > 0 && (
                      <span className="text-xs font-mono text-amber-400 flex-shrink-0">{parseFloat(item.cout_estime).toLocaleString('fr-FR')} EUR</span>
                    )}
                  </div>
                  );
                })}
                {(cat.hors_bordereau || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-dashed border-zinc-700">
                    <div className="text-[11px] uppercase tracking-wide text-amber-400 font-semibold mb-2">Hors bordereau</div>
                    <div className="space-y-1">
                      {cat.hors_bordereau.map((hb, hbi) => (
                        <div key={hbi} className="flex items-center gap-2 text-xs text-zinc-400">
                          <span className="flex-1 text-zinc-300">• {hb.label || '(sans intitulé)'}</span>
                          <span className="text-zinc-500">{hb.quantite} {hb.unite === 'm2' ? 'm²' : hb.unite} × {hb.prix_unitaire}€</span>
                          <span className="text-amber-400 font-mono w-20 text-right">{(parseFloat(hb.total) || 0).toLocaleString('fr-FR')} €</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Notes */}
      {(visit.general_notes || visit.travaux_prevus) && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-base text-white">Notes & Travaux prevus</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {visit.general_notes && (
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Notes generales</p>
                <p className="text-zinc-300 text-sm whitespace-pre-wrap">{visit.general_notes}</p>
              </div>
            )}
            {visit.travaux_prevus && (
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Travaux a prevoir</p>
                <p className="text-zinc-300 text-sm whitespace-pre-wrap">{visit.travaux_prevus}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={onClose}>
          Fermer
        </Button>
      </div>
    </div>
  );
};

// ===== Visit Form =====
// ============ BPU Autocomplete search ============
const BpuAutocomplete = ({ category, existingIds = [], onPick, testId }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = React.useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const items = await searchBordereau({ q, category, limit: 15 });
        const filtered = (items || []).filter((r) => !existingIds.includes(r.id));
        setResults(filtered);
        setHighlight(0);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [query, category, existingIds]);

  const pick = (item) => {
    onPick(item);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(results[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder={`Rechercher dans le bordereau « ${category} »...`}
          className="bg-zinc-950 border-zinc-800 text-white text-xs h-8 pl-7"
          data-testid={testId}
        />
      </div>
      {open && (results.length > 0 || query.trim()) && (
        <div className="absolute z-30 mt-1 w-full bg-zinc-950 border border-zinc-700 rounded-md shadow-xl max-h-80 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-zinc-500">Recherche…</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-zinc-500 italic">
              Aucun résultat. Utilisez « Ligne libre » si la prestation n'existe pas dans le BPU.
            </div>
          )}
          {results.map((r, i) => (
            <button
              key={r.id}
              type="button"
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(r)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 text-xs border-b border-zinc-800 last:border-0 ${i === highlight ? 'bg-emerald-500/10 text-white' : 'text-zinc-300 hover:bg-zinc-800'}`}
              data-testid={`bpu-result-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="truncate">{r.label}</div>
                {r.notes && <div className="text-[10px] text-zinc-500 truncate">{r.notes}</div>}
              </div>
              <span className="text-[10px] text-zinc-500 font-mono">{r.unite}</span>
              <span className="text-xs text-emerald-400 font-mono tabular-nums">{r.prix_mo.toFixed(2)} €</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const VisitForm = ({ visit, properties, onSave, onClose }) => {
  // Detect if the loaded visit had a manual override: stored budget differs from sum of items
  const computeInitialAuto = (v) => {
    if (!v?.id) return true;
    const sum = (v.categories || []).reduce((acc, cat) => {
      const itemsTotal = (cat.items || []).reduce((s, item) => s + (parseFloat(item.cout_estime) || 0), 0);
      const hbTotal = (cat.hors_bordereau || []).reduce((s, hb) => s + (parseFloat(hb.total) || 0), 0);
      return acc + itemsTotal + hbTotal;
    }, 0);
    const stored = parseFloat(v.budget_travaux_estime) || 0;
    // If difference < 1€ OR stored is 0 → considered auto-synced
    return Math.abs(stored - sum) < 1 || stored === 0;
  };

  const [formData, setFormData] = useState({
    property_id: visit?.property_id || '',
    visit_date: visit?.visit_date ? new Date(visit.visit_date) : new Date(),
    categories: visit?.categories || [],
    general_notes: visit?.general_notes || '',
    travaux_prevus: visit?.travaux_prevus || '',
    budget_travaux_estime: visit?.budget_travaux_estime || 0,
    // Budget auto-sync : true par défaut ; false uniquement si l'utilisateur a saisi un montant manuel différent du total des lignes
    budget_auto: computeInitialAuto(visit),
    is_viable: visit?.is_viable ?? null,
    photos: visit?.photos || [],
  });
  const [loading, setLoading] = useState(false);
  const [syncingCats, setSyncingCats] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const syncCategoriesWithBpu = async () => {
    if (!visit?.id) {
      // Visite non encore enregistrée : on rafraîchit juste depuis le template courant
      try {
        const data = await getVisitTemplate();
        const existing = formData.categories || [];
        const existingNames = new Set(existing.map(c => (c.name || '').trim()));
        const merged = [...existing];
        let added = 0;
        for (const c of data.categories || []) {
          if (!existingNames.has((c.name || '').trim())) {
            merged.push({ ...c, items: c.items || [], hors_bordereau: c.hors_bordereau || [] });
            added += 1;
          }
        }
        setFormData(prev => ({ ...prev, categories: merged }));
        toast.success(added ? `${added} nouvelle${added > 1 ? 's' : ''} catégorie${added > 1 ? 's' : ''} ajoutée${added > 1 ? 's' : ''}` : 'Cette visite est déjà à jour');
      } catch {
        toast.error('Impossible de synchroniser');
      }
      return;
    }
    setSyncingCats(true);
    try {
      const res = await axios.post(`${API}/visits/${visit.id}/sync-categories`, {}, { withCredentials: true });
      const added = res.data?.added || 0;
      if (added > 0) {
        // Recharger les catégories de la visite
        const fresh = await axios.get(`${API}/visits/${visit.id}`, { withCredentials: true });
        setFormData(prev => ({ ...prev, categories: fresh.data?.categories || prev.categories }));
        toast.success(res.data?.message || `${added} catégorie(s) ajoutée(s)`);
      } else {
        toast.info(res.data?.message || 'Cette visite est déjà à jour');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setSyncingCats(false);
    }
  };

  useEffect(() => {
    const fetchTemplate = async () => {
      if (formData.categories.length === 0) {
        try {
          const data = await getVisitTemplate();
          setFormData(prev => ({ ...prev, categories: data.categories || [] }));
        } catch (err) {
          console.error('Error fetching template:', err);
        }
      }
    };
    fetchTemplate();
  }, []);

  const selectedProperty = properties.find(p => p.id === formData.property_id);

  const handleCheckItem = (catIndex, itemIndex, status) => {
    setFormData(prev => {
      const newCategories = JSON.parse(JSON.stringify(prev.categories));
      newCategories[catIndex].items[itemIndex].status = status;
      // Keep backward compat
      newCategories[catIndex].items[itemIndex].checked = (status === 'conforme' || status === 'sans_objet');
      return { ...prev, categories: newCategories };
    });
  };

  const handleItemNote = (catIndex, itemIndex, notes) => {
    setFormData(prev => {
      const newCategories = JSON.parse(JSON.stringify(prev.categories));
      newCategories[catIndex].items[itemIndex].notes = notes;
      return { ...prev, categories: newCategories };
    });
  };

  const handleItemCost = (catIndex, itemIndex, cout_estime) => {
    setFormData(prev => {
      const newCategories = JSON.parse(JSON.stringify(prev.categories));
      const item = newCategories[catIndex].items[itemIndex];
      item.cout_estime = parseFloat(cout_estime) || 0;
      item.cost_override = true;  // flag : user manually overrode the BPU auto-calc
      return { ...prev, categories: newCategories };
    });
  };

  // Add a BPU item to a category (from autocomplete)
  const addBpuItemToCategory = (catIndex, bpuItem) => {
    setFormData(prev => {
      const newCategories = JSON.parse(JSON.stringify(prev.categories));
      newCategories[catIndex].items.push({
        name: bpuItem.label,
        status: 'a_remplacer',
        checked: false,
        bordereau_item_id: bpuItem.id,
        unite: bpuItem.unite || 'u',
        prix_unitaire: Number(bpuItem.prix_mo) || 0,
        quantite: 0,
        cout_estime: 0,
        notes: '',
        photo_ids: [],
      });
      return { ...prev, categories: newCategories };
    });
  };

  const removeItemFromCategory = (catIndex, itemIndex) => {
    setFormData(prev => {
      const newCategories = JSON.parse(JSON.stringify(prev.categories));
      newCategories[catIndex].items.splice(itemIndex, 1);
      return { ...prev, categories: newCategories };
    });
  };

  const handleItemQuantite = (catIndex, itemIndex, quantite) => {
    setFormData(prev => {
      const newCategories = JSON.parse(JSON.stringify(prev.categories));
      const item = newCategories[catIndex].items[itemIndex];
      const q = parseFloat(quantite) || 0;
      item.quantite = q;
      // Auto-compute cout_estime from BPU price unless user explicitly overrode
      if (!item.cost_override && item.prix_unitaire != null) {
        item.cout_estime = q * parseFloat(item.prix_unitaire || 0);
      }
      // Auto-flag status : any quantity > 0 means "à remplacer" (unless user set a specific status)
      if (q > 0 && (!item.status || item.status === 'non_verifie' || item.status === 'conforme')) {
        item.status = 'a_remplacer';
        item.checked = false;
      }
      // Reset to non_verifie if quantity set back to 0
      if (q === 0 && (item.status === 'a_remplacer' || item.status === 'a_remettre') && !item.notes) {
        item.status = 'non_verifie';
      }
      return { ...prev, categories: newCategories };
    });
  };

  // ---- Hors Bordereau (per category) ----
  const addHorsBordereau = (catIndex) => {
    setFormData(prev => {
      const newCategories = JSON.parse(JSON.stringify(prev.categories));
      if (!newCategories[catIndex].hors_bordereau) newCategories[catIndex].hors_bordereau = [];
      newCategories[catIndex].hors_bordereau.push({
        id: Math.random().toString(36).slice(2),
        label: '',
        unite: 'u',
        quantite: 0,
        prix_unitaire: 0,
        total: 0,
        notes: '',
      });
      return { ...prev, categories: newCategories };
    });
  };
  const updateHorsBordereau = (catIndex, hbIndex, field, value) => {
    setFormData(prev => {
      const newCategories = JSON.parse(JSON.stringify(prev.categories));
      const hb = newCategories[catIndex].hors_bordereau[hbIndex];
      hb[field] = ['quantite', 'prix_unitaire'].includes(field) ? (parseFloat(value) || 0) : value;
      hb.total = (parseFloat(hb.quantite) || 0) * (parseFloat(hb.prix_unitaire) || 0);
      return { ...prev, categories: newCategories };
    });
  };
  const removeHorsBordereau = (catIndex, hbIndex) => {
    setFormData(prev => {
      const newCategories = JSON.parse(JSON.stringify(prev.categories));
      newCategories[catIndex].hors_bordereau.splice(hbIndex, 1);
      return { ...prev, categories: newCategories };
    });
  };

  const handleItemPhoto = async (catIndex, itemIndex, fileList) => {
    if (!fileList || fileList.length === 0) return;
    try {
      const uploaded = [];
      const isOff = !navigator.onLine;
      for (const file of Array.from(fileList)) {
        if (isOff) {
          // Mode offline : compresser + stocker localement, upload sera différé
          const local = await storePhotoLocal({
            file,
            visit_id: visit?.id || null,
            project_id: null,
            folder: 'travaux',
            subfolder: 'Photos visite',
            filename: file.name,
          });
          uploaded.push({
            id: `local_${local.id}`,
            filename: local.filename,
            local: true,
            blob: local.blob,
          });
        } else {
          const fd = new FormData();
          fd.set('file', file);
          const params = ['folder=travaux', 'subfolder=' + encodeURIComponent('Photos visite')];
          try {
            const res = await axios.post(`${API}/upload?${params.join('&')}`, fd,
              { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            uploaded.push({ id: res.data.id, filename: res.data.filename });
          } catch {
            // Fallback : si l'upload réseau échoue, stocker en local
            const local = await storePhotoLocal({
              file,
              visit_id: visit?.id || null,
              folder: 'travaux',
              subfolder: 'Photos visite',
              filename: file.name,
            });
            uploaded.push({ id: `local_${local.id}`, filename: local.filename, local: true, blob: local.blob });
          }
        }
      }
      setFormData(prev => {
        const newCategories = JSON.parse(JSON.stringify(prev.categories));
        const existing = newCategories[catIndex].items[itemIndex].photo_ids || [];
        newCategories[catIndex].items[itemIndex].photo_ids = [...existing, ...uploaded.map(u => u.id)];
        // Auto-persist if editing existing visit
        if (visit?.id) {
          const payload = {
            property_id: prev.property_id,
            visit_date: prev.visit_date,
            categories: newCategories,
            general_notes: prev.general_notes,
            travaux_prevus: prev.travaux_prevus,
            budget_travaux_estime: parseFloat(prev.budget_travaux_estime) || 0,
            is_viable: prev.is_viable,
            photos: prev.photos || [],
          };
          // Use offline-aware updateVisit
          updateVisit(visit.id, payload).catch(() => {});
        }
        return { ...prev, categories: newCategories };
      });
      toast.success(isOff
        ? `${uploaded.length} photo(s) stockée(s) hors-ligne — upload différé`
        : `${uploaded.length} photo(s) ajoutée(s)`);
    } catch (e) {
      console.error(e);
      toast.error('Erreur photo');
    }
  };

  const handleRemoveItemPhoto = (catIndex, itemIndex, photoId) => {
    setFormData(prev => {
      const newCategories = JSON.parse(JSON.stringify(prev.categories));
      const photos = newCategories[catIndex].items[itemIndex].photo_ids || [];
      newCategories[catIndex].items[itemIndex].photo_ids = photos.filter(id => id !== photoId);
      return { ...prev, categories: newCategories };
    });
  };

  // Auto-calculate total budget from item costs
  const totalFromItems = useMemo(() => {
    return formData.categories.reduce((acc, cat) => {
      const itemsTotal = cat.items.reduce((s, item) => s + (parseFloat(item.cout_estime) || 0), 0);
      const hbTotal = (cat.hors_bordereau || []).reduce((s, hb) => s + (parseFloat(hb.total) || 0), 0);
      return acc + itemsTotal + hbTotal;
    }, 0);
  }, [formData.categories]);

  // Auto-sync budget_travaux_estime with totalFromItems, unless user has explicitly overridden it
  useEffect(() => {
    if (formData.budget_auto !== false) {
      setFormData((prev) => ({ ...prev, budget_travaux_estime: totalFromItems }));
    }
  }, [totalFromItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalItems = formData.categories.reduce((a, c) => a + c.items.length, 0);
  const checkedItems = formData.categories.reduce((a, c) => a + c.items.filter(i => isItemOk(i)).length, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property_id) {
      toast.error('Selectionnez un bien');
      return;
    }
    setLoading(true);
    try {
      const data = {
        ...formData,
        visit_date: formData.visit_date.toISOString(),
        budget_travaux_estime: parseFloat(formData.budget_travaux_estime) || totalFromItems,
      };

      if (visit?.id) {
        await updateVisit(visit.id, data);
        toast.success(navigator.onLine ? 'Visite mise à jour' : 'Visite mise à jour (hors-ligne, sera synchronisée)');
      } else {
        await createVisit(data);
        toast.success(navigator.onLine ? 'Visite enregistrée' : 'Visite enregistrée (hors-ligne, sera synchronisée)');
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-h-[80vh] overflow-y-auto pr-2">
      {/* Property Info Header */}
      <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
        <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wide mb-3">Informations du bien</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-300">Bien a visiter</Label>
            <Select value={formData.property_id} onValueChange={(v) => setFormData({ ...formData, property_id: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="visit-property-select">
                <SelectValue placeholder="Selectionner un bien" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.title} - {prop.city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-300">Date de visite</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start bg-zinc-950 border-zinc-800 text-white">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {format(formData.visit_date, 'dd/MM/yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                <Calendar
                  mode="single"
                  selected={formData.visit_date}
                  onSelect={(date) => { setFormData({ ...formData, visit_date: date || new Date() }); setCalendarOpen(false); }}
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        {selectedProperty && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-3 pt-3 border-t border-zinc-700">
            <div>
              <p className="text-xs text-zinc-500">Adresse</p>
              <p className="text-sm text-white">{selectedProperty.address}, {selectedProperty.postal_code} {selectedProperty.city}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Prix</p>
              <p className="text-sm text-emerald-400 font-mono">{selectedProperty.price?.toLocaleString('fr-FR')} EUR</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Surface</p>
              <p className="text-sm text-white">{selectedProperty.surface} m2</p>
            </div>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="flex items-center gap-4 flex-wrap">
        <Progress value={totalItems > 0 ? (checkedItems / totalItems) * 100 : 0} className="flex-1 min-w-[180px] h-2" />
        <span className="text-sm text-zinc-400 font-mono whitespace-nowrap">
          {checkedItems}/{totalItems} points ({totalItems > 0 ? Math.round(checkedItems / totalItems * 100) : 0}%)
        </span>
        <span className="text-sm text-amber-400 font-mono whitespace-nowrap">
          ~ {totalFromItems.toLocaleString('fr-FR')} EUR MO
        </span>
        <a href="/bordereau" target="_blank" rel="noreferrer"
          className="text-xs text-blue-400 hover:text-blue-300 underline-offset-2 hover:underline flex items-center gap-1 whitespace-nowrap"
          title="Ouvrir le bordereau de prix dans un nouvel onglet"
          data-testid="open-bordereau-ref">
          📖 Consulter le bordereau
        </a>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 h-8"
          onClick={syncCategoriesWithBpu}
          disabled={syncingCats}
          data-testid="sync-visit-categories"
          title="Récupère les nouvelles catégories de votre BPU dans cette visite (sans toucher aux items déjà cochés)"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncingCats ? 'animate-spin' : ''}`} />
          Mettre à jour les catégories
        </Button>
      </div>

      {/* Checklist - Full detail */}
      <Accordion type="multiple" className="space-y-2" defaultValue={formData.categories.map((_, i) => `cat-${i}`)}>
        {formData.categories.map((category, catIndex) => {
          const Icon = categoryIcons[category.name] || Wrench;
          const color = categoryColors[category.name] || 'text-zinc-400';
          const catChecked = category.items.filter(i => isItemOk(i)).length;
          const catItemsCost = category.items.reduce((s, i) => s + (parseFloat(i.cout_estime) || 0), 0);
          const catHbCost = (category.hors_bordereau || []).reduce((s, h) => s + (parseFloat(h.total) || 0), 0);
          const catCost = catItemsCost + catHbCost;

          return (
            <AccordionItem key={catIndex} value={`cat-${catIndex}`} className="border border-zinc-800 rounded-lg overflow-hidden">
              <AccordionTrigger className="px-4 py-3 bg-zinc-800/50 hover:bg-zinc-800 hover:no-underline">
                <div className="flex items-center gap-3 flex-1 mr-4">
                  <Icon className={`w-5 h-5 ${color}`} />
                  <span className="text-white font-medium text-sm">{category.name}</span>
                  <NewItemBadge addedAt={category.added_at} />
                  <div className="flex items-center gap-2 ml-auto">
                    {catCost > 0 && (
                      <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">
                        {catCost.toLocaleString('fr-FR')} EUR
                      </Badge>
                    )}
                    <Badge variant="outline" className={`text-xs ${catChecked === category.items.length
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : 'bg-zinc-700 text-zinc-400 border-zinc-600'
                      }`}>
                      {catChecked}/{category.items.length}
                    </Badge>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="bg-zinc-900 px-0 py-0">
                {/* Barre de recherche BPU : saisie du nommage des travaux */}
                <div className="px-4 pt-3 pb-2 border-b border-zinc-800">
                  <BpuAutocomplete
                    category={category.name}
                    existingIds={(category.items || []).map((i) => i.bordereau_item_id).filter(Boolean)}
                    onPick={(item) => addBpuItemToCategory(catIndex, item)}
                    testId={`bpu-search-${catIndex}`}
                  />
                </div>
                <div className="divide-y divide-zinc-800">
                  {category.items.length === 0 && (
                    <div className="px-4 py-6 text-center text-xs text-zinc-500 italic">
                      Aucune prestation. Tapez au-dessus pour rechercher dans le bordereau (ex: « tirag » → « Tirage de câble »).
                    </div>
                  )}
                  {category.items.map((item, itemIndex) => {
                    const itemSt = getItemStatus(item);
                    const stConfig = STATUS_OPTIONS[itemSt] || STATUS_OPTIONS.non_verifie;
                    return (
                    <div key={itemIndex} className={`px-4 py-3 ${itemSt === 'a_remettre' ? 'bg-red-500/5' : itemSt === 'conforme' ? 'bg-emerald-500/5' : ''}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm flex-1 min-w-[180px] text-zinc-300">{item.name}</span>
                        <button
                          type="button"
                          onClick={() => removeItemFromCategory(catIndex, itemIndex)}
                          className="text-zinc-500 hover:text-red-400 p-1"
                          title="Retirer cette ligne de la visite"
                          data-testid={`item-remove-${catIndex}-${itemIndex}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <Select value={itemSt} onValueChange={(v) => handleCheckItem(catIndex, itemIndex, v)}>
                          <SelectTrigger className={`w-40 h-7 text-xs border ${stConfig.bg} ${stConfig.color}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_OPTIONS).map(([k, v]) => (
                              <SelectItem key={k} value={k}><span className={v.color}>{v.label}</span></SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {/* BPU auto-chiffrage: qty x prix_unitaire = total MO */}
                        {item.bordereau_item_id && item.prix_unitaire > 0 ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number" min="0" step="0.5"
                              placeholder="Qté"
                              value={item.quantite || ''}
                              onChange={(e) => handleItemQuantite(catIndex, itemIndex, e.target.value)}
                              className="bg-zinc-950 border-zinc-800 text-white text-xs h-7 w-16 font-mono"
                              data-testid={`item-qty-${catIndex}-${itemIndex}`}
                            />
                            <span className="text-[10px] text-zinc-500 w-10">{item.unite === 'm2' ? 'm²' : item.unite === 'ml' ? 'ml' : item.unite === 'forfait' ? 'fft' : 'u'}</span>
                            <span className="text-[10px] text-zinc-600">×</span>
                            <span className="text-[11px] text-blue-400 font-mono w-14 text-right" title="Prix MO+matériel Bordereau">{item.prix_unitaire}€</span>
                            <span className="text-[10px] text-zinc-600">=</span>
                            <div className="flex items-center gap-1 w-24">
                              <Input type="number" value={item.cout_estime || ''} onChange={(e) => handleItemCost(catIndex, itemIndex, e.target.value)}
                                className={`bg-zinc-950 border-zinc-800 text-xs h-7 w-20 font-mono text-right ${item.cost_override ? 'text-amber-400' : 'text-emerald-400'}`}
                                title={item.cost_override ? "Coût forcé manuellement" : "Coût auto-calculé (BPU)"}
                                data-testid={`item-cost-${catIndex}-${itemIndex}`}
                              />
                              <span className="text-xs text-zinc-600">€</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 w-24">
                            <Input type="number" placeholder="Coût" value={item.cout_estime || ''} onChange={(e) => handleItemCost(catIndex, itemIndex, e.target.value)}
                              className="bg-zinc-950 border-zinc-800 text-white text-xs h-7 w-20" />
                            <span className="text-xs text-zinc-600">€</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-1">
                        <Input placeholder="Notes, observations..." value={item.notes} onChange={(e) => handleItemNote(catIndex, itemIndex, e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white text-xs h-7" />
                      </div>
                      {/* Photos per step */}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {(item.photo_ids || []).map(pid => (
                          <div key={pid} className="relative group/photo">
                            <img src={`${API}/files/${pid}`} alt="" className="h-14 w-14 object-cover rounded border border-zinc-700" />
                            <button type="button" onClick={() => handleRemoveItemPhoto(catIndex, itemIndex, pid)}
                              className="absolute -top-1 -right-1 bg-red-500 rounded-full w-4 h-4 flex items-center justify-center text-[10px] text-white opacity-0 group-hover/photo:opacity-100 transition-opacity">×</button>
                          </div>
                        ))}
                        <label className="cursor-pointer flex items-center gap-1.5 px-2 py-1 text-[10px] text-zinc-400 border border-dashed border-zinc-700 rounded hover:border-zinc-500 hover:text-white transition-colors">
                          <Camera className="w-3 h-3" /> Photo
                          <input type="file" accept="image/*" capture="environment" className="hidden"
                            onChange={(e) => { handleItemPhoto(catIndex, itemIndex, e.target.files); e.target.value = ''; }}
                            data-testid={`item-photo-${catIndex}-${itemIndex}`} />
                        </label>
                      </div>
                    </div>
                    );
                  })}
                </div>

                {/* ===== Hors Bordereau : lignes libres par catégorie ===== */}
                <div className="border-t border-zinc-800 bg-zinc-900/60 px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wide text-amber-400 font-semibold flex items-center gap-1.5">
                      <Plus className="w-3 h-3" /> Hors bordereau
                      {(category.hors_bordereau || []).length > 0 && (
                        <span className="text-zinc-500 normal-case font-normal">
                          — {(category.hors_bordereau || []).reduce((s, hb) => s + (parseFloat(hb.total) || 0), 0).toLocaleString('fr-FR')} €
                        </span>
                      )}
                    </div>
                    <Button type="button" size="sm" variant="outline"
                      className="h-6 border-zinc-700 text-[10px] text-zinc-400 hover:text-white"
                      onClick={() => addHorsBordereau(catIndex)}
                      data-testid={`add-hors-bordereau-${catIndex}`}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Ajouter une ligne libre
                    </Button>
                  </div>
                  {(category.hors_bordereau || []).length > 0 && (
                    <div className="space-y-1.5">
                      {(category.hors_bordereau || []).map((hb, hbIndex) => (
                        <div key={hb.id || hbIndex} className="flex flex-wrap items-center gap-1.5 p-2 bg-zinc-950/40 border border-dashed border-zinc-700 rounded" data-testid={`hb-row-${catIndex}-${hbIndex}`}>
                          <Input placeholder="Intitulé (ex: trappe coupe-feu)" value={hb.label || ''}
                            onChange={(e) => updateHorsBordereau(catIndex, hbIndex, 'label', e.target.value)}
                            className="bg-zinc-950 border-zinc-800 text-white text-xs h-7 flex-1 min-w-[140px]" />
                          <Input type="number" min="0" step="0.5" placeholder="Qté" value={hb.quantite || ''}
                            onChange={(e) => updateHorsBordereau(catIndex, hbIndex, 'quantite', e.target.value)}
                            className="bg-zinc-950 border-zinc-800 text-white text-xs h-7 w-16 font-mono" />
                          <Select value={hb.unite || 'u'} onValueChange={(v) => updateHorsBordereau(catIndex, hbIndex, 'unite', v)}>
                            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white text-xs h-7 w-20"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="m2">m²</SelectItem>
                              <SelectItem value="ml">ml</SelectItem>
                              <SelectItem value="u">unité</SelectItem>
                              <SelectItem value="forfait">forfait</SelectItem>
                            </SelectContent>
                          </Select>
                          <span className="text-[10px] text-zinc-600">×</span>
                          <Input type="number" min="0" step="0.01" placeholder="PU" value={hb.prix_unitaire || ''}
                            onChange={(e) => updateHorsBordereau(catIndex, hbIndex, 'prix_unitaire', e.target.value)}
                            className="bg-zinc-950 border-zinc-800 text-white text-xs h-7 w-20 font-mono" />
                          <span className="text-[10px] text-zinc-600">€ =</span>
                          <span className="text-xs font-mono text-amber-400 w-20 text-right">
                            {(parseFloat(hb.total) || 0).toLocaleString('fr-FR')} €
                          </span>
                          <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-zinc-500 hover:text-red-400"
                            onClick={() => removeHorsBordereau(catIndex, hbIndex)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Notes & Budget Summary */}
      <Card className="bg-zinc-800/50 border-zinc-700">
        <CardContent className="p-4 space-y-4">
          <div>
            <Label className="text-zinc-300">Notes generales de visite</Label>
            <Textarea
              value={formData.general_notes}
              onChange={(e) => setFormData({ ...formData, general_notes: e.target.value })}
              className="bg-zinc-950 border-zinc-800 text-white min-h-[80px] mt-1"
              placeholder="Etat general, impressions, points d'attention..."
            />
          </div>
          <div>
            <Label className="text-zinc-300">Detail des travaux a prevoir</Label>
            <Textarea
              value={formData.travaux_prevus}
              onChange={(e) => setFormData({ ...formData, travaux_prevus: e.target.value })}
              className="bg-zinc-950 border-zinc-800 text-white min-h-[80px] mt-1"
              placeholder="Lister les travaux par priorite, devis a demander..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-300 flex items-center justify-between">
                Budget total travaux (EUR)
                {formData.budget_auto === false && (
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, budget_auto: true, budget_travaux_estime: totalFromItems }))}
                    className="text-[10px] text-emerald-400 hover:text-emerald-300 underline"
                  >
                    Resync auto
                  </button>
                )}
              </Label>
              <Input
                type="number"
                value={formData.budget_travaux_estime}
                onChange={(e) => setFormData({ ...formData, budget_travaux_estime: e.target.value, budget_auto: false })}
                className="bg-zinc-950 border-zinc-800 text-white mt-1"
                placeholder={`Auto: ${totalFromItems.toLocaleString('fr-FR')}`}
              />
              <p className="text-xs text-zinc-500 mt-1">
                {formData.budget_auto === false ? 'Saisie manuelle' : 'Synchro auto avec les lignes'} · Somme postes: {totalFromItems.toLocaleString('fr-FR')} EUR
              </p>
            </div>
            <div>
              <Label className="text-zinc-300">Verdict viabilite</Label>
              <Select
                value={formData.is_viable === null ? 'pending' : formData.is_viable ? 'viable' : 'not_viable'}
                onValueChange={(v) => setFormData({ ...formData, is_viable: v === 'pending' ? null : v === 'viable' })}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">En cours d'evaluation</SelectItem>
                  <SelectItem value="viable">Viable</SelectItem>
                  <SelectItem value="not_viable">Non viable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800 sticky bottom-0 bg-zinc-900 pb-2">
        <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">
          Annuler
        </Button>
        <Button type="submit" disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-visit-button">
          {visit?.id ? 'Mettre a jour' : 'Enregistrer la visite'}
        </Button>
      </div>
    </form>
  );
};

// ===== Visit Card =====
const VisitCard = ({ visit, property, onView, onEdit, onDelete, onExportPdf, onNav, highlight }) => {
  const checkedCount = visit.categories?.reduce((acc, cat) =>
    acc + cat.items.filter(item => isItemOk(item)).length, 0) || 0;
  const totalCount = visit.categories?.reduce((acc, cat) =>
    acc + cat.items.length, 0) || 0;
  const totalCost = visit.categories?.reduce((acc, cat) =>
    acc + cat.items.reduce((s, i) => s + (parseFloat(i.cout_estime) || 0), 0), 0) || 0;
  const pct = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;

  return (
    <Card className={`bg-zinc-900 border-zinc-800 card-hover ${highlight ? 'ring-2 ring-emerald-500' : ''}`} id={`visit-${visit.id}`}>
      <CardContent className="p-5">
        {visit._local_status === 'pending' && (
          <div className="mb-2 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300" data-testid={`visit-pending-${visit.id}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
            En attente de synchronisation
          </div>
        )}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-white">{property?.title || 'Bien non trouve'}</h3>
            <div className="flex items-center gap-1 text-sm text-zinc-500 mt-1">
              <MapPin className="w-3 h-3" />
              {property?.address}, {property?.city}
            </div>
          </div>
          <div className={`px-2.5 py-1 rounded-md text-xs font-semibold ${visit.is_viable === true ? 'bg-emerald-500/15 text-emerald-400' :
              visit.is_viable === false ? 'bg-red-500/15 text-red-400' :
                'bg-zinc-700 text-zinc-400'
            }`}>
            {visit.is_viable === true ? 'Viable' : visit.is_viable === false ? 'Non viable' : 'En cours'}
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm text-zinc-400 mb-3">
          <CalendarIcon className="w-4 h-4" />
          {visit.visit_date ? format(new Date(visit.visit_date), 'dd MMMM yyyy', { locale: fr }) : 'Date non definie'}
        </div>

        {property?.price && (
          <div className="text-sm font-mono text-emerald-400 mb-3">
            Prix: {property.price.toLocaleString('fr-FR')} EUR
          </div>
        )}

        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-zinc-500">{checkedCount}/{totalCount} points</span>
              <span className="text-xs text-zinc-500">{pct}%</span>
            </div>
            <Progress value={pct} className="h-1.5" />
          </div>
        </div>

        {(totalCost > 0 || visit.budget_travaux_estime > 0) && (
          <div className="bg-zinc-800/50 rounded p-2 mb-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">Travaux estimes</span>
              <span className="text-sm font-mono text-amber-400">
                {(totalCost || visit.budget_travaux_estime || 0).toLocaleString('fr-FR')} EUR
              </span>
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" className="flex-1 border-zinc-700 text-zinc-300" onClick={() => onView(visit)}>
            <Eye className="w-4 h-4 mr-1" /> Rapport
          </Button>
          <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => onEdit(visit)} data-testid="edit-visit-button">
            <FileText className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" className="border-zinc-700 text-emerald-400 hover:text-emerald-300" onClick={() => onExportPdf(visit.id)} data-testid="export-visit-pdf">
            <Download className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="destructive" onClick={() => onDelete(visit.id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        {/* Cross-navigation shortcuts */}
        {visit.property_id && (
          <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-zinc-800/60">
            <button onClick={() => onNav(`/recherche?property_id=${visit.property_id}`)}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-[10px] text-zinc-400 hover:text-white"
              data-testid={`nav-property-${visit.id}`}>
              <Building2 className="w-3 h-3" /> Bien
            </button>
            <button onClick={() => onNav(`/etude?property_id=${visit.property_id}`)}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-emerald-500/20 hover:text-emerald-400 text-[10px] text-zinc-400"
              data-testid={`nav-study-${visit.id}`}>
              <Calculator className="w-3 h-3" /> Étude
            </button>
            <button onClick={() => onNav(`/pipeline?property_id=${visit.property_id}`)}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-purple-500/20 hover:text-purple-400 text-[10px] text-zinc-400"
              data-testid={`nav-pipeline-${visit.id}`}>
              <Kanban className="w-3 h-3" /> Pipeline
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===== Main Page =====
export const VisitsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [visits, setVisits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [highlightVisitId, setHighlightVisitId] = useState(null);

  const fetchData = async () => {
    try {
      const [visitsList, propsList] = await Promise.all([
        listVisits(),
        listProperties(),
      ]);
      setVisits(visitsList);
      setProperties(propsList);
    } catch (err) {
      const cached = await listVisits().catch(() => []);
      setVisits(cached);
      if (navigator.onLine) toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Handle cross-nav : ?property_id=... → highlight existing visit OR open new visit form pre-filled
  useEffect(() => {
    const propId = searchParams.get('property_id');
    if (!propId || loading) return;
    const existing = visits.find(v => v.property_id === propId);
    if (existing) {
      setHighlightVisitId(existing.id);
      setTimeout(() => {
        const el = document.getElementById(`visit-${existing.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    } else {
      setSelectedVisit({ property_id: propId });
      setFormOpen(true);
      toast.info("Aucune visite pour ce bien — création d'une nouvelle");
    }
    searchParams.delete('property_id');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visits, loading, searchParams]);

  const handleView = (visit) => {
    setSelectedVisit(visit);
    setReportOpen(true);
  };

  const handleEdit = (visit) => {
    setSelectedVisit(visit);
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const isLocal = String(id).startsWith('local_') || (await import('../offline/repo')).then;
      if (!navigator.onLine) {
        await deleteVisit(id);
        toast.success('Visite supprimée (hors-ligne, sera synchronisée)');
        fetchData();
        return;
      }
      const res = await axios.delete(`${API}/visits/${id}`, { withCredentials: true });
      // Aussi nettoyer le cache local
      await deleteVisit(id).catch(() => null);
      const c = res.data?.cascade || {};
      const parts = [];
      if (c.bdcs_deleted) parts.push(`${c.bdcs_deleted} BDC supprimé(s)`);
      if (c.bdcs_detached) parts.push(`${c.bdcs_detached} BDC détaché(s)`);
      toast.success(parts.length > 0 ? `Visite supprimée · ${parts.join(', ')}` : 'Visite supprimée');
      fetchData();
    } catch (err) {
      console.error('Delete visit error:', err);
      // Fallback : enqueue for later
      await deleteVisit(id);
      toast.success('Visite supprimée (sync en attente)');
      fetchData();
    }
  };

  const handleCloseForm = () => { setFormOpen(false); setSelectedVisit(null); };
  const handleCloseReport = () => { setReportOpen(false); setSelectedVisit(null); };

  const handleExportPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/visits/${id}/pdf`, { withCredentials: true, responseType: 'blob' });
      downloadBlob(res.data, `visite_${id.slice(0, 8)}.pdf`, 'application/pdf');
      toast.success('PDF téléchargé');
    } catch (err) {
      console.error('Export visit PDF error:', err);
      toast.error('Erreur export PDF');
    }
  };

  const getPropertyById = (id) => properties.find(p => p.id === id);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="visits-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Visites techniques</h1>
          <p className="text-zinc-400 mt-1">Compte-rendus detailles pour chaque bien</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setLoading(true); fetchData(); }} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm transition-colors" data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
          <Button
            className="bg-white text-black hover:bg-zinc-200"
            onClick={() => { setSelectedVisit(null); setFormOpen(true); }}
            data-testid="add-visit-button"
          >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle visite
        </Button>
        </div>
      </div>

      {/* Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto w-[95vw]">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">
              {selectedVisit?.id ? 'Modifier la visite' : 'Nouvelle visite technique'}
            </DialogTitle>
          </DialogHeader>
          <VisitForm
            visit={selectedVisit}
            properties={properties}
            onSave={fetchData}
            onClose={handleCloseForm}
          />
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto w-[95vw]">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">
              Rapport de visite
            </DialogTitle>
          </DialogHeader>
          {selectedVisit && (
            <VisitReport
              visit={selectedVisit}
              property={getPropertyById(selectedVisit.property_id)}
              onClose={handleCloseReport}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-64 bg-zinc-800 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : visits.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visits.map((visit) => (
            <VisitCard
              key={visit.id}
              visit={visit}
              property={getPropertyById(visit.property_id)}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onExportPdf={handleExportPdf}
              onNav={navigate}
              highlight={visit.id === highlightVisitId}
            />
          ))}
        </div>
      ) : (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-16 text-center">
            <ClipboardCheck className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Aucune visite</h3>
            <p className="text-zinc-500 mb-4">Planifiez votre premiere visite technique</p>
            <Button
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              onClick={() => { setSelectedVisit(null); setFormOpen(true); }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Creer un compte-rendu
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
